import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { api, errorMessage, useApiMutation, useApiQuery } from "../../api";
import type { Uuid } from "../../api";
import { Badge, Button, Card, Field, useToast } from "../../components";
import { formatTokens, parseTokenInput } from "../../format";
import { AsyncBoundary } from "../../shell";
import feuilles from "../feuilles/Feuilles.module.css";
import parametres from "../parametres/Parametres.module.css";

export type LimiteCreditProps = {
  companyId: Uuid;
  /** Seul l'administrateur ESN fixe la limite ; le commercial la lit. */
  editable: boolean;
};

/**
 * Le prélèvement automatique d'une entreprise vu par l'ESN : où en est le
 * client (mandat, pay as you go) et la limite de crédit, qui est le risque
 * d'impayé que l'ESN accepte.
 */
export function LimiteCredit({ companyId, editable }: LimiteCreditProps) {
  const compte = useApiQuery((o) => api.getCompanyBilling(companyId, o), [companyId]);
  return (
    <Card
      title="Paiement et limite de crédit"
      subtitle="Le client signe son mandat et active le pay as you go ; l'ESN fixe jusqu'où il peut engager au-delà de ses packs."
    >
      <AsyncBoundary state={compte} errorTitle="Paiement illisible">
        {(a) => (
          <div className={parametres.formulaire}>
            <div className={parametres.ligne}>
              {a.payment_method === "active" ? <Badge tone="success">Mandat actif</Badge> : <Badge tone="neutral">Pas de mandat</Badge>}
              {a.pay_as_you_go ? <Badge tone="accent">Pay as you go activé</Badge> : <Badge tone="neutral">Pay as you go coupé</Badge>}
              {a.topup_enabled ? <Badge tone="accent">Recharge automatique</Badge> : <Badge tone="neutral">Sans recharge</Badge>}
              {a.suspended_at !== null && <Badge tone="danger">Suspendu</Badge>}
              {a.payment_method_label !== null && <span>{a.payment_method_label}</span>}
            </div>
            {a.suspended_at !== null && <p className={feuilles.mention}>{a.suspension_reason}</p>}
            <FormulaireLimite companyId={companyId} limite={a.credit_limit_tokens} editable={editable} onUpdated={compte.reload} />
          </div>
        )}
      </AsyncBoundary>
    </Card>
  );
}

type FormulaireLimiteProps = { companyId: Uuid; limite: string; editable: boolean; onUpdated: () => void };

function FormulaireLimite({ companyId, limite, editable, onUpdated }: FormulaireLimiteProps) {
  const { notify } = useToast();
  const [saisie, setSaisie] = useState(formatTokens(limite));
  useEffect(() => setSaisie(formatTokens(limite)), [limite]);
  const maj = useApiMutation((tokens: string) => api.setCompanyCreditLimit(companyId, { credit_limit_tokens: tokens }));

  const lu = parseTokenInput(saisie);

  async function soumettre(e: FormEvent) {
    e.preventDefault();
    if (lu === null) {
      return;
    }
    const a = await maj.run(lu);
    if (a === null) {
      return;
    }
    onUpdated();
    notify({ tone: "success", title: "Limite de crédit enregistrée", description: `${formatTokens(a.credit_limit_tokens)} tokens.` });
  }

  if (!editable) {
    return (
      <p className={feuilles.mention}>
        Limite de crédit : <strong>{formatTokens(limite)} tokens</strong> — fixée par un administrateur ESN.
      </p>
    );
  }

  const erreur = lu === null ? "Un nombre de tokens positif, deux décimales au plus." : maj.error !== null ? errorMessage(maj.error) : undefined;
  return (
    <form className={parametres.formulaire} onSubmit={(e) => void soumettre(e)} noValidate>
      <Field label="Limite de crédit" hint="En tokens. 0 rend le pay as you go inopérant." error={erreur}>
        {(commande) => (
          <input {...commande} type="text" inputMode="decimal" value={saisie} onChange={(e) => setSaisie(e.target.value)} disabled={maj.pending} />
        )}
      </Field>
      <Button type="submit" variant="primary" loading={maj.pending} disabled={lu === null || lu === limite}>
        Enregistrer la limite
      </Button>
    </form>
  );
}
